"use client";

import type { RefObject } from "react";
import { SHAPE } from "@/components/tools/geometry/diagram";
import type { UnitCircleAngle } from "@/lib/tool-kit/geometry/unit-circle";

/*
 * The unit-circle chart: a circle of radius 1 with the angles marked, degrees and
 * radians outside, and each point's exact (cos θ, sin θ) written along its radius
 * inside. Every angle is a button, so a reader (or a keyboard) can select one.
 */

export interface UnitCircleShow {
  degrees: boolean;
  radians: boolean;
  coordinates: boolean;
  tangent: boolean;
  quadrants: boolean;
}

export interface UnitCircleSvgProps {
  angles: UnitCircleAngle[];
  show: UnitCircleShow;
  selected: number | null;
  onSelect: (degrees: number) => void;
  svgRef: RefObject<SVGSVGElement | null>;
  title: string;
  heading: string;
  formula: string;
  quadrantNotes: Record<1 | 2 | 3 | 4, string>;
  /** The accessible name of an angle's button: "30 degrees". */
  angleLabel: (degrees: number) => string;
  /** The label for 0°, which is also 360°. */
  zeroLabel: string;
}

const WIDTH = 660;
const HEIGHT = 700;
const CX = 330;
const CY = 372;
const R = 205;
const FONT = "system-ui, -apple-system, Segoe UI, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
const ROMAN = { 1: "I", 2: "II", 3: "III", 4: "IV" } as const;
const TAN_COLOR = "#7a4b00";

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

function pointAt(degrees: number, radius: number) {
  const angle = toRadians(degrees);
  return {
    x: CX + radius * Math.cos(angle),
    y: CY - radius * Math.sin(angle),
  };
}

/** Text laid along a radius: upright on the right half, flipped on the left half. */
function RadialText({
  degrees,
  lines,
  size,
}: {
  degrees: number;
  lines: { text: string; color: string }[];
  size: number;
}) {
  const left = Math.cos(toRadians(degrees)) < -0.001;
  const rotation = left ? 180 - degrees : -degrees;
  const inner = 38;
  const gap = size + 3;
  const first = -((lines.length - 1) * gap) / 2 + size / 3;
  return (
    <g
      pointerEvents="none"
      transform={`translate(${CX} ${CY}) rotate(${rotation})`}
    >
      {lines.map((line, index) => (
        <text
          fill={line.color}
          fontFamily={MONO}
          fontSize={size}
          key={line.text}
          textAnchor={left ? "end" : "start"}
          x={left ? -inner : inner}
          y={first + index * gap}
        >
          {line.text}
        </text>
      ))}
    </g>
  );
}

function OuterLabel({
  angle,
  show,
  zeroLabel,
}: {
  angle: UnitCircleAngle;
  show: UnitCircleShow;
  zeroLabel: string;
}) {
  const { x, y } = pointAt(angle.degrees, R + 16);
  const cos = Math.cos(toRadians(angle.degrees));
  const sin = Math.sin(toRadians(angle.degrees));
  let anchor: "start" | "middle" | "end" = "middle";
  if (cos > 0.25) {
    anchor = "start";
  } else if (cos < -0.25) {
    anchor = "end";
  }
  const lines: { text: string; bold: boolean; color: string }[] = [];
  if (show.degrees) {
    lines.push({
      text: angle.degrees === 0 ? zeroLabel : `${angle.degrees}°`,
      bold: true,
      color: SHAPE.ink,
    });
  }
  if (show.radians) {
    lines.push({
      text: angle.degrees === 0 ? "0 = 2π" : angle.radians,
      bold: false,
      color: SHAPE.ink,
    });
  }
  if (show.tangent) {
    lines.push({ text: `tan = ${angle.tan}`, bold: false, color: TAN_COLOR });
  }
  const height = lines.length * 15;
  const top = y - height / 2 - (sin * height) / 2;
  return (
    <>
      {lines.map((line, index) => (
        <text
          fill={line.color}
          fontFamily={FONT}
          fontSize={line.bold ? 14 : 12.5}
          fontWeight={line.bold ? 600 : 400}
          key={line.text}
          textAnchor={anchor}
          x={x}
          y={top + 12 + index * 15}
        >
          {line.text}
        </text>
      ))}
    </>
  );
}

function Highlight({ angle }: { angle: UnitCircleAngle }) {
  const p = pointAt(angle.degrees, R);
  return (
    <g pointerEvents="none">
      <line
        stroke={SHAPE.accent}
        strokeWidth={3}
        x1={CX}
        x2={p.x}
        y1={CY}
        y2={p.y}
      />
      <line
        stroke={SHAPE.guide}
        strokeDasharray="6 4"
        strokeWidth={2.5}
        x1={p.x}
        x2={p.x}
        y1={CY}
        y2={p.y}
      />
      <line
        stroke={SHAPE.stroke}
        strokeWidth={4}
        x1={CX}
        x2={p.x}
        y1={CY}
        y2={CY}
      />
    </g>
  );
}

function QuadrantNotes({ notes }: { notes: Record<1 | 2 | 3 | 4, string> }) {
  const spots = {
    1: { x: WIDTH - 14, y: 118, anchor: "end" },
    2: { x: 14, y: 118, anchor: "start" },
    3: { x: 14, y: HEIGHT - 44, anchor: "start" },
    4: { x: WIDTH - 14, y: HEIGHT - 44, anchor: "end" },
  } as const;
  return (
    <>
      {([1, 2, 3, 4] as const).map((q) => (
        <g key={q}>
          <text
            fill={SHAPE.muted}
            fontFamily={FONT}
            fontSize={26}
            fontWeight={600}
            textAnchor={spots[q].anchor}
            x={spots[q].x}
            y={spots[q].y}
          >
            {ROMAN[q]}
          </text>
          <text
            fill={SHAPE.muted}
            fontFamily={FONT}
            fontSize={12}
            textAnchor={spots[q].anchor}
            x={spots[q].x}
            y={spots[q].y + 18}
          >
            {notes[q]}
          </text>
        </g>
      ))}
    </>
  );
}

export function UnitCircleSvg(props: UnitCircleSvgProps) {
  const { angles, show, selected } = props;
  const dense = angles.length > 16;
  const chosen = angles.find((angle) => angle.degrees === selected);
  return (
    // biome-ignore lint/a11y/useSemanticElements: an SVG figure holding focusable angle points, not a fieldset
    <svg
      aria-label={props.title}
      className="uc-sheet mx-auto block h-auto w-full max-w-3xl rounded-lg border border-border bg-white print:max-w-none print:rounded-none print:border-0"
      fontFamily={FONT}
      ref={props.svgRef}
      role="group"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect fill="#ffffff" height={HEIGHT} width={WIDTH} />
      <text
        fill={SHAPE.ink}
        fontSize={26}
        fontWeight={600}
        textAnchor="middle"
        x={CX}
        y={38}
      >
        {props.heading}
      </text>
      <text fill={SHAPE.muted} fontSize={14} textAnchor="middle" x={CX} y={62}>
        {props.formula}
      </text>
      {show.quadrants ? <QuadrantNotes notes={props.quadrantNotes} /> : null}
      <line
        stroke={SHAPE.muted}
        strokeWidth={1.2}
        x1={CX - R - 12}
        x2={CX + R + 12}
        y1={CY}
        y2={CY}
      />
      <line
        stroke={SHAPE.muted}
        strokeWidth={1.2}
        x1={CX}
        x2={CX}
        y1={CY - R - 12}
        y2={CY + R + 12}
      />
      <circle
        cx={CX}
        cy={CY}
        fill="none"
        r={R}
        stroke={SHAPE.ink}
        strokeWidth={2.5}
      />
      {chosen ? <Highlight angle={chosen} /> : null}
      {show.coordinates
        ? angles.map((angle) => {
            const lines = [
              { text: `(${angle.cos}, ${angle.sin})`, color: SHAPE.ink },
            ];
            return (
              <RadialText
                degrees={angle.degrees}
                key={angle.degrees}
                lines={lines}
                size={dense ? 10 : 12.5}
              />
            );
          })
        : null}
      {angles.map((angle) => {
        const p = pointAt(angle.degrees, R);
        const isSelected = angle.degrees === selected;
        return (
          // biome-ignore lint/a11y/useSemanticElements: an SVG point cannot be a <button>
          <g
            aria-label={props.angleLabel(angle.degrees)}
            aria-pressed={isSelected}
            cursor="pointer"
            key={angle.degrees}
            onClick={() => props.onSelect(angle.degrees)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                props.onSelect(angle.degrees);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <circle cx={p.x} cy={p.y} fill="transparent" r={15} />
            <circle
              cx={p.x}
              cy={p.y}
              fill={isSelected ? SHAPE.accent : SHAPE.ink}
              r={isSelected ? 7 : 4.5}
              stroke="#ffffff"
              strokeWidth={1.5}
            />
            <OuterLabel angle={angle} show={show} zeroLabel={props.zeroLabel} />
          </g>
        );
      })}
    </svg>
  );
}
